import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import logo from '../assets/vpchemtechlogo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setProductsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    {
      name: 'Products',
      path: '/products',
      dropdown: [
        { name: 'All Products', path: '/products' },
        { name: 'RU DSA-6', path: '/products/ru-dsa-6' },
        { name: 'RU ORA-7', path: '/products/ru-ora-7' },
        { name: 'V-MUKTI 24', path: '/products/v-mukti-24' },
      ],
    },
    { name: 'Industries', path: '/industries' },
    // { name: 'Our Success', path: '/success' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-md py-2'
          : 'bg-white/95 backdrop-blur-md py-3 shadow-sm'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative flex items-center space-x-3 z-10">
            <img
              src={logo}
              alt="VP Chem Technologies"
              className="pointer-events-none select-none absolute left-0 top-1/2 -translate-y-1/2 h-56 md:h-72 lg:h-96 w-auto object-contain transition-all duration-300 origin-left scale-125 md:scale-150 lg:scale-150"
            />
            {/* Invisible box just to reserve horizontal space without changing navbar height */}
            <div className="h-0 w-36 md:w-44 lg:w-52"></div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <Link
                    to={link.path}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-1 ${
                      location.pathname.startsWith('/products')
                        ? 'text-vp-green-600 bg-vp-cream-200'
                        : 'text-vp-grey-800 hover:text-vp-green-600 hover:bg-vp-cream-200'
                    }`}
                  >
                    <span>{link.name}</span>
                    <FiChevronDown className="text-sm" />
                  </Link>
                  {/* Dropdown */}
                  <div
                    className={`absolute top-full left-0 mt-2 w-48 bg-vp-cream-50 shadow-lg rounded-lg overflow-hidden transition-all duration-300 ${
                      productsOpen
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-3 text-vp-grey-700 hover:bg-vp-cream-200 hover:text-vp-green-600 transition-colors duration-200"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'text-vp-green-600 bg-vp-cream-200'
                      : 'text-vp-grey-800 hover:text-vp-green-600 hover:bg-vp-cream-200'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* CTA Button - Desktop */}
          <Link
            to="/contact"
            className="hidden lg:block px-6 py-3 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300"
          >
            Get Started
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-vp-grey-800 hover:text-vp-green-600 transition-colors duration-300"
          >
            {isOpen ? <FiX size={28} /> : <FiMenu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? 'max-h-screen mt-4' : 'max-h-0'
          }`}
        >
          <div className="flex flex-col space-y-2 pb-4">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name}>
                  <button
                    onClick={() => setProductsOpen(!productsOpen)}
                    className="w-full text-left px-4 py-3 rounded-lg font-medium text-vp-grey-800 hover:bg-vp-cream-200 hover:text-vp-green-600 transition-colors duration-300 flex items-center justify-between"
                  >
                    <span>{link.name}</span>
                    <FiChevronDown
                      className={`transition-transform duration-300 ${
                        productsOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {productsOpen && (
                    <div className="ml-4 mt-2 space-y-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block px-4 py-2 rounded-lg text-vp-grey-700 hover:bg-vp-cream-200 hover:text-vp-green-600 transition-colors duration-200"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-vp-green-600 bg-vp-cream-200'
                      : 'text-vp-grey-800 hover:bg-vp-cream-200 hover:text-vp-green-600'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="mt-4 px-6 py-3 bg-gradient-to-r from-vp-green-600 to-vp-blue-600 text-white font-semibold rounded-lg text-center hover:shadow-lg transition-all duration-300"
            >
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
